import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getTransactionSummaryReport,
  exportReportXlsx,
  exportReportPdf,
} from "../store/slices/businessOwnerSlice";
import { showApiError, showApiSuccess } from "../utils/toast";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// Get current date in UTC to avoid timezone issues
const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1; // 1-based

// Generate year options from 2024 to current year
const YEARS = Array.from(
  { length: currentYear - 2023 },
  (_, i) => currentYear - i
);

// Helper to check if a month/year is in the future
const isMonthDisabled = (year, month) => {
  return year > currentYear || (year === currentYear && month > currentMonth);
};

// Helper to get available months for a given year
const getAvailableMonths = (year) => {
  if (year < currentYear) {
    // Past years: all 12 months available
    return MONTHS;
  } else if (year === currentYear) {
    // Current year: only months up to current month
    return MONTHS.filter((m) => m.value <= currentMonth);
  }
  // Future years: no months available
  return [];
};

export const useBusinessReport = () => {
  const dispatch = useDispatch();
  const { report, reportLoading, reportError, exportLoading } = useSelector(
    (state) => state.businessOwner
  );

  // Helper function to get default date range for a month
  const getDefaultDateRange = (year, month) => {
    // Create dates using UTC to avoid timezone issues
    const monthStart = new Date(Date.UTC(year, month - 1, 1));
    const monthEnd = new Date(Date.UTC(year, month, 0));
    
    // Format as YYYY-MM-DD (using UTC to avoid timezone offset)
    const formatUTC = (date) => {
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, '0');
      const d = String(date.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    return {
      from_date: formatUTC(monthStart),
      to_date: formatUTC(monthEnd),
    };
  };

  const defaultDateRange = getDefaultDateRange(currentYear, currentMonth);

  const [filters, setFilters] = useState({
    year: currentYear,
    month: currentMonth,
    from_date: defaultDateRange.from_date,
    to_date: defaultDateRange.to_date,
    page: 1,
    per_page: 15,
  });

  // Fetch report whenever filters change
  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.year, filters.month, filters.from_date, filters.to_date, filters.page, filters.per_page]);

  const fetchReport = useCallback(async () => {
    try {
      await dispatch(
        getTransactionSummaryReport({
          year: filters.year,
          month: filters.month,
          from_date: filters.from_date,
          to_date: filters.to_date,
          page: filters.page,
          per_page: filters.per_page,
        })
      );
    } catch (err) {
      showApiError(null, "Failed to load report");
    }
  }, [dispatch, filters]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const updated = {
        ...prev,
        [key]: value,
        // Reset to page 1 whenever year/month/date changes
        ...(key === "year" || key === "month" || key === "from_date" || key === "to_date" ? { page: 1 } : {}),
      };

      // When year or month changes, auto-update date range to full month
      if (key === "year" || key === "month") {
        const newYear = key === "year" ? value : updated.year;
        const newMonth = key === "month" ? value : updated.month;
        const newDateRange = getDefaultDateRange(newYear, newMonth);
        updated.from_date = newDateRange.from_date;
        updated.to_date = newDateRange.to_date;
      }

      // Validate date range is within the selected month
      if (key === "from_date" || key === "to_date") {
        const monthStart = new Date(Date.UTC(updated.year, updated.month - 1, 1));
        const monthEnd = new Date(Date.UTC(updated.year, updated.month, 0));
        
        // Parse dates safely
        const parseUTCDate = (dateStr) => {
          const [y, m, d] = dateStr.split('-').map(Number);
          return new Date(Date.UTC(y, m - 1, d));
        };
        
        const formatUTCDate = (date) => {
          const y = date.getUTCFullYear();
          const m = String(date.getUTCMonth() + 1).padStart(2, '0');
          const d = String(date.getUTCDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        };
        
        const fromDateObj = parseUTCDate(updated.from_date);
        const toDateObj = parseUTCDate(updated.to_date);

        // Clamp from_date to month start if it's before
        if (fromDateObj < monthStart) {
          updated.from_date = formatUTCDate(monthStart);
        }

        // Clamp to_date to month end if it's after
        if (toDateObj > monthEnd) {
          updated.to_date = formatUTCDate(monthEnd);
        }
      }

      return updated;
    });
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  // Trigger browser download from a blob URL
  const triggerDownload = (blobUrl, filename) => {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  };

  const handleExportXlsx = useCallback(async () => {
    try {
      const result = await dispatch(
        exportReportXlsx({
          year: filters.year,
          month: filters.month,
          from_date: filters.from_date,
          to_date: filters.to_date,
        })
      );
      if (exportReportXlsx.fulfilled.match(result)) {
        triggerDownload(result.payload.blobUrl, result.payload.filename);
        showApiSuccess("XLSX downloaded successfully");
      } else {
        showApiError(null, result.payload || "Failed to export XLSX");
      }
    } catch (err) {
      showApiError(null, "Failed to export XLSX");
    }
  }, [dispatch, filters.year, filters.month, filters.from_date, filters.to_date]);

  const handleExportPdf = useCallback(async () => {
    try {
      const result = await dispatch(
        exportReportPdf({
          year: filters.year,
          month: filters.month,
          from_date: filters.from_date,
          to_date: filters.to_date,
        })
      );
      if (exportReportPdf.fulfilled.match(result)) {
        triggerDownload(result.payload.blobUrl, result.payload.filename);
        showApiSuccess("PDF downloaded successfully");
      } else {
        showApiError(null, result.payload || "Failed to export PDF");
      }
    } catch (err) {
      showApiError(null, "Failed to export PDF");
    }
  }, [dispatch, filters.year, filters.month, filters.from_date, filters.to_date]);

  // Derived summary totals from the current page data
  const summaryTotals = useCallback(() => {
    const transactions = Array.isArray(report?.data) ? report.data : [];
    return {
      totalBillAmount: transactions.reduce((sum, t) => sum + (t.bill_amount || 0), 0),
      totalCustomerDiscount: transactions.reduce((sum, t) => sum + (t.customer_discount || 0), 0),
      totalPlatformCommission: transactions.reduce((sum, t) => sum + (t.platform_commission || 0), 0),
      totalFinalAmount: transactions.reduce((sum, t) => sum + (t.final_amount || 0), 0),
      totalCount: report?.pagination?.total || transactions.length,
    };
  }, [report]);

  const selectedMonthLabel =
    MONTHS.find((m) => m.value === filters.month)?.label || "";

  return {
    // Data
    report,
    transactions: Array.isArray(report?.data) ? report.data : [],
    pagination: report?.pagination || null,
    filter: report?.filter || null,
    stats: report?.stats || null,
    summaryTotals: summaryTotals(),

    // State
    loading: reportLoading,
    error: reportError,
    exportLoading,

    // Filters
    filters,
    updateFilter,
    handlePageChange,
    selectedMonthLabel,

    // Constants
    MONTHS,
    YEARS,
    
    // Helpers
    isMonthDisabled,
    getAvailableMonths,

    // Actions
    handleExportXlsx,
    handleExportPdf,
    refreshReport: fetchReport,
  };
};

export default useBusinessReport;
